import React from 'react';
import { keyLiveProjects, freelanceProjects } from '../content_helper';

const imgClass = "w-full h-full object-cover opacity-80 mix-blend-luminosity hover:opacity-100 hover:mix-blend-normal transition-all duration-700";

const TechBadge = ({ label }) => (
  <span className="px-3 py-1 rounded-md bg-primary/5 text-primary text-[10px] font-bold uppercase border border-primary/10 tracking-widest">
    {label}
  </span>
);

const Projects = () => {
  const institutionalProjects = keyLiveProjects.filter(p => p.type === 'institutional' && p.image);
  const [metrash, qatarRail] = institutionalProjects;
  const bentoProjects = keyLiveProjects.filter(p => ['hayya', 'hukoomi'].includes(p.id));

  return (
    <main className="pt-48 pb-stack-lg max-w-container-max mx-auto px-margin-page">
      {/* Hero Title */}
      <section className="mb-stack-lg pb-16 relative">
        <div className="max-w-4xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-10 h-[1px] bg-primary"></div>
            <span className="font-label uppercase tracking-[0.3em] text-[11px] text-primary font-bold">Selected Works 2024</span>
          </div>
          <h1 className="font-headline text-headline-xl mb-10 font-bold tracking-tight text-white leading-[1.05]">
            Engineering Digital<br /><span className="text-primary/90">Ecosystems</span>
          </h1>
          <p className="font-body text-body-lg max-w-2xl text-on-surface-variant font-light leading-relaxed">
            A showcase of large-scale institutional platforms, smart automation, and high-performance applications developed with architectural precision.
          </p>
        </div>
      </section>

      {/* Featured Projects Grid */}
      <section className="mb-stack-lg">
        <div className="flex items-center gap-8 mb-12">
          <h2 className="font-headline text-headline-lg font-semibold tracking-tight text-white">Institutional Portfolio</h2>
          <div className="h-[1px] flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Metrash */}
          {metrash && (
            <div className="glacier-card rounded-xl overflow-hidden">
              <div className="h-72 relative bg-surface-container-highest/30 overflow-hidden">
                <img alt={metrash.name} className={imgClass} src={metrash.image} />
                <div className="absolute top-4 left-4 glacier-glass px-4 py-1.5 rounded-full font-label text-[10px] uppercase font-bold text-primary tracking-widest border border-primary/20">
                  Featured Case
                </div>
              </div>
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="font-headline text-2xl font-bold text-white mb-2">{metrash.name}</h3>
                    <p className="font-label text-xs uppercase tracking-widest text-primary font-bold">{metrash.subtitle}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl glacier-glass flex items-center justify-center text-primary border border-primary/20">
                    <span className="material-symbols-outlined text-[28px]">{metrash.icon}</span>
                  </div>
                </div>
                <p className="font-body text-on-surface-variant font-light mb-8 leading-relaxed">{metrash.description}</p>
                <div className="flex flex-wrap gap-2 mb-10">
                  {metrash.tech.slice(0, 3).map(t => <TechBadge key={t} label={t} />)}
                </div>
                {/* <a className="inline-flex items-center gap-3 text-primary font-label text-label-md uppercase font-bold group" href="/">
                  View Case Study <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </a> */}
              </div>
            </div>
          )}

          {/* Qatar Rail */}
          {qatarRail && (
            <div className="glacier-card rounded-xl overflow-hidden">
              <div className="h-72 relative bg-surface-container-highest/30 overflow-hidden">
                <img alt={qatarRail.name} className={imgClass} src={qatarRail.image} />
                <div className="absolute top-4 left-4 glacier-glass px-4 py-1.5 rounded-full font-label text-[10px] uppercase font-bold text-primary tracking-widest border border-primary/20">
                  Transportation
                </div>
              </div>
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="font-headline text-2xl font-bold text-white mb-2">{qatarRail.name}</h3>
                    <p className="font-label text-xs uppercase tracking-widest text-primary font-bold">{qatarRail.subtitle}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl glacier-glass flex items-center justify-center text-primary border border-primary/20">
                    <span className="material-symbols-outlined text-[28px]">{qatarRail.icon}</span>
                  </div>
                </div>
                <p className="font-body text-on-surface-variant font-light mb-8 leading-relaxed">{qatarRail.description}</p>
                <div className="flex flex-wrap gap-2 mb-10">
                  {qatarRail.tech.slice(0, 3).map(t => <TechBadge key={t} label={t} />)}
                </div>
                {/* <a className="inline-flex items-center gap-3 text-primary font-label text-label-md uppercase font-bold group" href="/">
                  Explore Platform <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">open_in_new</span>
                </a> */}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bento Grid – Hayya & Hukoomi */}
      <section className="mb-stack-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {bentoProjects.map(proj => (
            <div
              key={proj.id}
              className={`glacier-card rounded-xl overflow-hidden group ${proj.id === 'hayya' ? 'md:col-span-2' : ''}`}
            >
              <div className="h-48 relative bg-surface-container-highest/30 overflow-hidden">
                <img alt={proj.name} className={imgClass} src={proj.image} />
              </div>
              <div className="p-8 flex flex-col items-center text-center justify-center">
                <h3 className="font-headline text-2xl font-bold text-white mb-4">{proj.name}</h3>
                <p className="font-body text-sm text-on-surface-variant font-light mb-8 leading-relaxed">{proj.description}</p>
                <div className="flex flex-wrap gap-3 justify-center text-primary/60 font-mono text-[10px] font-bold uppercase tracking-widest mb-6">
                  {proj.tech.slice(0, 3).map(t => <span key={t}>{t}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Freelance & Innovation */}
      <section>
        <div className="flex items-center gap-8 mb-12">
          <h2 className="font-headline text-headline-lg font-semibold tracking-tight text-white">Freelance &amp; Innovation</h2>
          <div className="h-[1px] flex-grow bg-gradient-to-r from-primary/50 to-transparent"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {freelanceProjects.map(proj => (
            <div key={proj.id} className="glacier-card rounded-xl overflow-hidden hover:bg-surface-container-high/40 transition-colors">
              {proj.image && (
                <div className="h-48 relative bg-surface-container-highest/30 overflow-hidden">
                  <img alt={proj.name} className={imgClass} src={proj.image} />
                </div>
              )}
              <div className="p-10">
                <div className="flex justify-between items-center mb-8">
                  <h4 className="font-headline text-2xl font-bold text-white">{proj.name}</h4>
                  <a href={proj.link || '/'}
                    className="w-10 h-10 rounded-lg glacier-glass flex items-center justify-center text-primary border border-primary/10 hover:bg-primary/20 transition-colors">
                    <span className="material-symbols-outlined text-[24px]">open_in_new</span>
                  </a>
                </div>
                <p className="font-body text-on-surface-variant font-light mb-10 leading-relaxed border-l border-primary/30 pl-6">
                  {proj.description}
                </p>
                <div className="flex gap-3 flex-wrap">
                  {proj.tech.map(t => <TechBadge key={t} label={t} />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Projects;
